import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useConfirmModal } from "./adminShared";

const fmtDateTime = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

/**
 * AdminLegalTerms — editor dos Termos de Uso. Cada save incrementa a versão;
 * tenants com accepted_terms_version desatualizado veem o gate modal no
 * próximo login (ver TermsGateModal.js).
 */
const AdminLegalTerms = ({ onBack }) => {
    const [content, setContent] = useState("");
    const [version, setVersion] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const { requestConfirm, ConfirmModalElement } = useConfirmModal();

    const load = async () => {
        setLoading(true);
        try {
            const doc = await api.get("/admin/legal/terms");
            setContent(doc.content || "");
            setVersion(doc.version);
            setUpdatedAt(doc.updated_at);
            setDirty(false);
        } catch (e) {
            toast.error("Falha ao carregar Termos de Uso", { description: e?.message || "Tente novamente." });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const handleSave = () => {
        requestConfirm({
            title: "Publicar nova versão",
            message: "Salvar vai incrementar a versão dos Termos de Uso. Todos os tenants precisarão aceitar novamente no próximo login. Continuar?",
            confirmLabel: "Salvar",
            onConfirm: async () => {
                setSaving(true);
                try {
                    const doc = await api.patch("/admin/legal/terms", { content });
                    setVersion(doc.version);
                    setUpdatedAt(doc.updated_at);
                    setDirty(false);
                    toast.success("Termos atualizados", { description: `Nova versão: ${doc.version}` });
                } catch (e) {
                    toast.error("Falha ao salvar", { description: e?.message || "Tente novamente." });
                } finally {
                    setSaving(false);
                }
            },
        });
    };

    return (
        <div className="space-y-6" data-testid="admin-legal-terms-view">
            <header className="flex items-center gap-3">
                {onBack && (
                    <button
                        data-testid="admin-legal-terms-back"
                        onClick={onBack}
                        className="h-10 w-10 rounded-xl border border-white/10 hover:border-indigo-500/40 text-slate-400 hover:text-indigo-300 flex items-center justify-center transition-all shrink-0"
                    >
                        <i className="fa-solid fa-arrow-left"></i>
                    </button>
                )}
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Plataforma · Configurações</div>
                    <h1 className="salon-heading text-3xl text-white mt-1">Termos de Uso</h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Edite o conteúdo exibido no cadastro público e no aviso de atualização que todo tenant vê ao logar.
                    </p>
                </div>
            </header>

            <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-5" data-testid="admin-legal-terms-editor">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4 text-xs">
                    <div className="flex items-center gap-4">
                        <span className="text-slate-400">
                            Versão atual: <b className="text-white font-mono">{version ?? "—"}</b>
                        </span>
                        <span className="text-slate-500">Atualizado em: {fmtDateTime(updatedAt)}</span>
                    </div>
                    {dirty && (
                        <span className="text-amber-300 uppercase tracking-wider text-[10px] font-bold">
                            <i className="fa-solid fa-circle-exclamation mr-1"></i>Alterações não salvas
                        </span>
                    )}
                </div>

                {loading ? (
                    <div className="py-16 text-center text-slate-500 text-sm">Carregando…</div>
                ) : (
                    <textarea
                        data-testid="admin-legal-terms-textarea"
                        value={content}
                        onChange={(e) => { setContent(e.target.value); setDirty(true); }}
                        rows={20}
                        placeholder="Escreva o conteúdo dos Termos de Uso…"
                        className="w-full rounded-xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-slate-200 outline-none focus:border-indigo-500/60 transition-colors font-mono leading-relaxed resize-y"
                    />
                )}

                <div className="mt-4 flex items-center justify-end gap-2">
                    <button
                        data-testid="admin-legal-terms-reload"
                        onClick={load}
                        disabled={loading || saving}
                        className="min-h-[44px] px-4 rounded-xl border border-white/10 text-slate-300 hover:text-white hover:border-white/20 text-xs uppercase tracking-wider transition-all disabled:opacity-50"
                    >
                        Descartar
                    </button>
                    <button
                        data-testid="admin-legal-terms-save"
                        onClick={handleSave}
                        disabled={loading || saving || !dirty}
                        className="min-h-[44px] px-5 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white font-semibold text-xs uppercase tracking-wider transition-all disabled:opacity-40"
                    >
                        {saving ? <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Salvando…</> : "Salvar (incrementa versão)"}
                    </button>
                </div>
            </div>

            {ConfirmModalElement}
        </div>
    );
};

export default AdminLegalTerms;
